import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloseIcon from '@mui/icons-material/Close';
import { Alert } from "@mui/material";
import { useAuth } from "./auth/AuthContext";
import { db } from "./firebase";
import {
  doc,
  runTransaction,
  serverTimestamp,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";

import { sample_data as SAMPLE_DATA } from './week3';

/**
 * Material UI Leopardy Game (JSON-driven)
 * ----------------------------------------------------------
 * Drop this file into a React app (e.g., Vite) as src/LeopardyGame.jsx
 * and import it in App.jsx. Uses MUI Grid/Stack per docs.
 *
 * Data format (see SAMPLE_DATA below):
 * {
 *   title: string,
 *   teams: string[],
 *   categories: [
 *     {
 *       name: string,
 *       clues: [{ value: number, question: string, answer: string }]
 *     }, ...
 *   ]
 * }
 */



// ---------- Styled helpers ----------
const BoardPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: 16,
}));

const TileButton = styled(Button)(({ theme }) => ({
  height: 84,
  fontWeight: 900,
  letterSpacing: 1,
  fontSize: 20,
  color: '#fbeec7',
  background: '#8c5c2c',
  border: '2px solid rgba(31, 20, 11, 0.4)',
  boxShadow: '0 6px 0 rgba(31, 20, 11, 0.35)',
  '&:hover': { background: '#9d6a32' },
  '&:disabled': {
    background: '#5a4125',
    color: 'rgba(251, 238, 199, 0.6)',
    boxShadow: 'none',
  },
}));

const Muted = ({ children }) => (
  <Typography variant="body2" color="text.secondary">{children}</Typography>
);

const safariPalette = {
  sand: '#f5e2c4',
  stone: 'rgba(31, 20, 11, 0.65)',
  leather: '#8c5c2c',
  leatherDeep: '#3b2a1a',
  acacia: '#6b8f3f',
  sunrise: '#e7c35f',
};

// Subtle repeating leopard print without external assets
const leopardPrint = {
  backgroundColor: '#d9b26d',
  backgroundImage: `
    radial-gradient(circle at 20% 25%, rgba(53, 34, 14, 0.8) 0, rgba(53, 34, 14, 0.8) 16px, transparent 18px),
    radial-gradient(circle at 70% 35%, rgba(53, 34, 14, 0.7) 0, rgba(53, 34, 14, 0.7) 14px, transparent 17px),
    radial-gradient(circle at 35% 70%, rgba(53, 34, 14, 0.75) 0, rgba(53, 34, 14, 0.75) 18px, transparent 21px),
    radial-gradient(circle at 80% 75%, rgba(53, 34, 14, 0.65) 0, rgba(53, 34, 14, 0.65) 15px, transparent 18px),
    radial-gradient(circle at 55% 55%, rgba(0, 0, 0, 0.18) 0, rgba(0, 0, 0, 0.18) 32px, transparent 36px)
  `,
  backgroundSize: '180px 180px',
  backgroundRepeat: 'repeat',
};

const BUZZ_DURATION_MS = 33000;

function buildEmptyMatrix(data) {
  return data.categories.map((cat) => cat.clues.map(() => ({ revealed: false, done: false })));
}

function matrixToFlat(matrix) {
  return matrix.flatMap((col, cIdx) =>
    col.map((cell, rIdx) => ({ cIdx, rIdx, revealed: !!cell.revealed, done: !!cell.done }))
  );
}

function flatToMatrix(flat, data) {
  const base = buildEmptyMatrix(data);
  if (!Array.isArray(flat)) return base;
  flat.forEach((cell) => {
    if (cell && typeof cell.cIdx === "number" && typeof cell.rIdx === "number") {
      const { cIdx, rIdx } = cell;
      if (base[cIdx] && base[cIdx][rIdx]) {
        base[cIdx][rIdx] = { revealed: !!cell.revealed, done: !!cell.done };
      }
    }
  });
  return base;
}

function makeId() {
  return (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// ---------- Component ----------
export default function Leopadry({ data = SAMPLE_DATA, gameId, role = "viewer", gameState }) {
  const { user } = useAuth();
  const isRealtime = !!gameId;

  // Local-only state (used for quick-play when no gameId)
  const [localBoard, setLocalBoard] = useState(() =>
    data.categories.map((cat) => cat.clues.map(() => ({ revealed: false, done: false })))
  );
  const [localActiveClue, setLocalActiveClue] = useState(null);
  const [localShowAnswer, setLocalShowAnswer] = useState(false);
  const [localTeams, setLocalTeams] = useState(() => []);
  const [localPendingTeams, setLocalPendingTeams] = useState([]);
  const [joinName, setJoinName] = useState('');

  const derivedBoard = useMemo(() => {
    if (!isRealtime) return localBoard;
    if (gameState?.board) {
      if (Array.isArray(gameState.board[0])) return gameState.board;
      return flatToMatrix(gameState.board, data);
    }
    return buildEmptyMatrix(data);
  }, [isRealtime, localBoard, gameState?.board, data]);

  const derivedActiveClue = isRealtime ? gameState?.activeClue || null : localActiveClue;
  const derivedShowAnswer = isRealtime ? !!gameState?.showAnswer : localShowAnswer;
  const derivedTeams = useMemo(
    () => (isRealtime ? gameState?.teams || [] : localTeams),
    [isRealtime, gameState?.teams, localTeams]
  );
  const derivedPending = isRealtime ? gameState?.pendingTeams || [] : localPendingTeams;
  const derivedBuzzer = isRealtime ? gameState?.buzzer || null : null;
  const isHost = role === "host";
  const isPlayer = role === "player";
  const derivedWinner = isRealtime ? gameState?.winner || null : null;
  const [countdownMs, setCountdownMs] = useState(null);
  const audioRef = useRef(null);

  const rowCount = useMemo(
    () => Math.max(...data.categories.map((cat) => cat.clues.length)),
    [data]
  );

  const current = useMemo(() => {
    if (!derivedActiveClue) return null;
    const { catIdx, clueIdx } = derivedActiveClue;
    return data.categories[catIdx].clues[clueIdx];
  }, [derivedActiveClue, data]);

  // ---------- Realtime helpers ----------
  const docRef = isRealtime ? doc(db, "games_leopardy", gameId) : null;

  const ensureState = async () => {
    if (!isRealtime || role !== "host") return;
    if (gameState?.board) return;
    const baseBoard = buildEmptyMatrix(data);
    await updateDoc(docRef, {
      state: {
        board: matrixToFlat(baseBoard),
        activeClue: null,
        showAnswer: false,
        teams: [],
        pendingTeams: [],
        buzzer: null,
        lastUpdate: serverTimestamp(),
      }
    });
  };

  useEffect(() => {
    ensureState().catch((err) => console.error("init state failed", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRealtime]);

  const pickClue = async (catIdx, clueIdx) => {
    if (isRealtime) {
      if (role !== "host") return;
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(docRef);
        const g = snap.data() || {};
        const st = g.state || {};
        const boardMatrix = Array.isArray(st.board?.[0]) ? st.board : flatToMatrix(st.board, data);
        if (boardMatrix[catIdx][clueIdx]?.done) return;
        boardMatrix[catIdx][clueIdx] = { ...(boardMatrix[catIdx][clueIdx] || {}), revealed: true };
        tx.update(docRef, {
          state: {
            ...st,
            board: matrixToFlat(boardMatrix),
            activeClue: { catIdx, clueIdx, startedAt: serverTimestamp(), value: data.categories[catIdx].clues[clueIdx].value },
            showAnswer: false,
            buzzer: null,
            teams: (st.teams || []).map((t) => ({ ...t, disabled: false })),
            lastUpdate: serverTimestamp(),
          }
        });
      });
      return;
    }
    if (derivedBoard[catIdx][clueIdx].done) return;
    setLocalActiveClue({ catIdx, clueIdx });
    setLocalShowAnswer(false);
    setLocalBoard((b) => {
      const copy = b.map((col) => col.map((c) => ({ ...c })));
      copy[catIdx][clueIdx].revealed = true;
      return copy;
    });
  };

  const markDone = () => {
    if (isRealtime) {
      if (!derivedActiveClue || role !== "host") return;
      const { catIdx, clueIdx } = derivedActiveClue;
      updateDoc(docRef, {
        "state.board": matrixToFlat(
          derivedBoard.map((col, ci) =>
            col.map((cell, ri) => {
              if (ci === catIdx && ri === clueIdx) return { ...cell, done: true, revealed: true };
              return cell;
            })
          )
        ),
        "state.activeClue": null,
        "state.buzzer": null,
        "state.showAnswer": false,
        "state.lastUpdate": serverTimestamp(),
        "state.teams": (gameState?.state?.teams || gameState?.teams || []).map((t) => ({ ...t, disabled: false })),
      }).catch((err) => console.error("markDone", err));
      return;
    }
    if (!localActiveClue) return;
    setLocalBoard((b) => {
      const copy = b.map((col) => col.map((c) => ({ ...c })));
      copy[localActiveClue.catIdx][localActiveClue.clueIdx].done = true;
      return copy;
    });
    setLocalActiveClue(null);
  };

  const awardPoints = (teamIdx, sign = +1) => {
    if (!current) return;
    if (isRealtime) {
      if (role !== "host") return;
      runTransaction(db, async (tx) => {
        const snap = await tx.get(docRef);
        const st = (snap.data() || {}).state || {};
        const teams = st.teams || [];
        const target = teams[teamIdx];
        if (!target) return;
        const value = st.activeClue?.value || current.value || 0;
        const delta = sign * value;
        const updated = teams.map((t, i) => i === teamIdx ? { ...t, score: (t.score || 0) + delta } : t);
        const { catIdx, clueIdx } = st.activeClue || {};
        const boardMatrix = Array.isArray(st.board?.[0]) ? st.board : flatToMatrix(st.board, data);
        const nextMatrix = boardMatrix.map((col, ci) =>
          col.map((cell, ri) => {
            if (ci === catIdx && ri === clueIdx && sign > 0) return { ...cell, done: true, revealed: true };
            return cell;
          })
        );
        tx.update(docRef, {
          state: {
            ...st,
            teams: updated,
            board: matrixToFlat(nextMatrix),
            activeClue: sign > 0 ? null : st.activeClue,
            buzzer: sign > 0 ? null : null,
            showAnswer: false,
            winner: null,
            lastUpdate: serverTimestamp(),
          }
        });
      }).catch((err) => console.error("awardPoints", err));
      return;
    }
    const delta = sign * (current.value || 0);
    setLocalTeams((ts) => ts.map((t, i) => (i === teamIdx ? { ...t, score: t.score + delta } : t)));
    if (sign > 0) markDone();
  };

  const resetBoard = () => {
    if (isRealtime) {
      if (role !== "host") return;
      const fresh = buildEmptyMatrix(data);
      updateDoc(docRef, {
        state: {
          board: matrixToFlat(fresh),
          activeClue: null,
          showAnswer: false,
          teams: [],
          pendingTeams: [],
          buzzer: null,
          lastUpdate: serverTimestamp(),
        }
      }).catch((err) => console.error("resetBoard", err));
      return;
    }
    setLocalBoard(data.categories.map((cat) => cat.clues.map(() => ({ revealed: false, done: false }))));
    setLocalActiveClue(null);
    setLocalShowAnswer(false);
    setLocalTeams((ts) => ts.map((t) => ({ ...t, score: 0 })));
  };

  const requestJoin = (name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (isRealtime) {
      if (!user) return;
      const req = { id: makeId(), name: trimmed, uid: user.uid, ts: Date.now() };
      updateDoc(docRef, {
        "state.pendingTeams": arrayUnion(req),
        "state.lastUpdate": serverTimestamp(),
      }).catch((err) => console.error("join", err));
      setJoinName('');
      return;
    }
    setLocalPendingTeams((p) => [...p, { id: makeId(), name: trimmed }]);
    setJoinName('');
  };

  const approveTeam = (id) => {
    if (isRealtime) {
      runTransaction(db, async (tx) => {
        const snap = await tx.get(docRef);
        const st = (snap.data() || {}).state || {};
        const pending = st.pendingTeams || [];
        const found = pending.find((t) => t.id === id);
        if (!found) return;
        const teams = st.teams || [];
        tx.update(docRef, {
          state: {
            ...st,
            pendingTeams: pending.filter((t) => t.id !== id),
            teams: [...teams, { ...found, score: 0, disabled: false }],
            lastUpdate: serverTimestamp(),
          }
        });
      }).catch((err) => console.error("approve", err));
      return;
    }
    setLocalPendingTeams((p) => {
      const found = p.find((t) => t.id === id);
      if (!found) return p;
      setLocalTeams((ts) => [...ts, { name: found.name, score: 0 }]);
      return p.filter((t) => t.id !== id);
    });
  };

  const rejectTeam = (id) => {
    if (isRealtime) {
      updateDoc(docRef, {
        "state.pendingTeams": (derivedPending || []).filter((t) => t.id !== id),
        "state.lastUpdate": serverTimestamp(),
      }).catch((err) => console.error("reject", err));
      return;
    }
    setLocalPendingTeams((p) => p.filter((t) => t.id !== id));
  };

  // ---------- Buzzer handling (realtime only) ----------
  const myTeam = useMemo(
    () => derivedTeams.find((t) => t.uid === user?.uid),
    [derivedTeams, user?.uid]
  );

  const buzzIn = async () => {
    if (!isRealtime || !myTeam || !derivedActiveClue) return;
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(docRef);
      const st = (snap.data() || {}).state || {};
      if (!st.activeClue) return;
      const buzzer = st.buzzer;
      const now = Date.now();
      if (buzzer && buzzer.expiresAt && buzzer.expiresAt.toMillis && buzzer.expiresAt.toMillis() > now) return;
      if (buzzer && buzzer.expiresAt && buzzer.expiresAt > now) return;
      const teams = st.teams || [];
      const me = teams.find((t) => t.id === myTeam.id);
      if (!me || me.disabled) return;
      const expires = new Date(now + BUZZ_DURATION_MS);
      tx.update(docRef, {
        state: {
          ...st,
          buzzer: {
            teamId: myTeam.id,
            lockedAt: serverTimestamp(),
            expiresAt: expires,
          },
          lastUpdate: serverTimestamp(),
        }
      });
    });
  };

  const handleBuzzTimeout = async () => {
    if (!isRealtime || role !== "host") return;
    const buzzer = derivedBuzzer;
    if (!buzzer || !buzzer.expiresAt) return;
    const expiry = buzzer.expiresAt.toMillis ? buzzer.expiresAt.toMillis() : new Date(buzzer.expiresAt).getTime();
    if (Date.now() <= expiry) return;
    const value = derivedActiveClue?.value || current?.value || 0;
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(docRef);
      const st = (snap.data() || {}).state || {};
      const teams = (st.teams || []).map((t) => {
        if (t.id === buzzer.teamId) {
          return { ...t, score: (t.score || 0) - value, disabled: true };
        }
        return t;
      });
      tx.update(docRef, {
        state: {
          ...st,
          teams,
          buzzer: null,
          lastUpdate: serverTimestamp(),
        }
      });
    });
  };

  useEffect(() => {
    if (!isRealtime || role !== "host") return;
    const timer = setInterval(() => {
      handleBuzzTimeout().catch((err) => console.error("buzz timeout", err));
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedBuzzer, derivedActiveClue, role, isRealtime]);

  const handleIncorrect = (teamId) => {
    if (!isRealtime || role !== "host" || !derivedActiveClue) return;
    runTransaction(db, async (tx) => {
      const snap = await tx.get(docRef);
      const st = (snap.data() || {}).state || {};
      const value = st.activeClue?.value || current?.value || 0;
      const teams = (st.teams || []).map((t) => t.id === teamId ? { ...t, score: (t.score || 0) - value, disabled: true } : t);
      tx.update(docRef, {
        state: {
          ...st,
          teams,
          buzzer: null,
          showAnswer: false,
          lastUpdate: serverTimestamp(),
        }
      });
    }).catch((err) => console.error("incorrect", err));
  };

  const handleCorrect = (teamId) => {
    if (!isRealtime || role !== "host" || !derivedActiveClue) return;
    const { catIdx, clueIdx } = derivedActiveClue;
    runTransaction(db, async (tx) => {
      const snap = await tx.get(docRef);
      const st = (snap.data() || {}).state || {};
      const value = st.activeClue?.value || current?.value || 0;
      const teams = (st.teams || []).map((t) => t.id === teamId ? { ...t, score: (t.score || 0) + value } : t);
      const boardMatrix = Array.isArray(st.board?.[0]) ? st.board : flatToMatrix(st.board, data);
      const nextMatrix = boardMatrix.map((col, ci) =>
        col.map((cell, ri) => {
          if (ci === catIdx && ri === clueIdx) return { ...cell, done: true, revealed: true };
          return cell;
        })
      );
      const allDone = nextMatrix.every((col) => col.every((c) => c.done));
      const winner =
        allDone && teams.length > 0
          ? teams.reduce((best, t) => (t.score > (best?.score ?? -Infinity) ? t : best), null)
          : null;
      tx.update(docRef, {
        state: {
          ...st,
          teams,
          board: matrixToFlat(nextMatrix),
          activeClue: null,
          buzzer: null,
          showAnswer: false,
          winner: winner ? { id: winner.id, name: winner.name, score: winner.score } : null,
          lastUpdate: serverTimestamp(),
        }
      });
    }).catch((err) => console.error("correct", err));
  };

  const canBuzz = Boolean(isRealtime && role === "player" && myTeam && derivedActiveClue && (!derivedBuzzer || derivedBuzzer.teamId === myTeam.id) && !myTeam.disabled);
  const currentBuzzerTeam = derivedBuzzer ? derivedTeams.find((t) => t.id === derivedBuzzer.teamId) : null;

  useEffect(() => {
    if (!derivedBuzzer) {
      setCountdownMs(null);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
      return;
    }
    const expiry = derivedBuzzer.expiresAt?.toMillis ? derivedBuzzer.expiresAt.toMillis() : new Date(derivedBuzzer.expiresAt).getTime();
    const updateCountdown = () => {
      const left = Math.max(0, expiry - Date.now());
      setCountdownMs(left);
    };
    updateCountdown();
    const id = setInterval(updateCountdown, 200);

    // Play jeopardy theme from public/audio/jeopardy.mp3
    if (!audioRef.current) {
      try {
        const audio = new Audio("/audio/jeopardy.mp3");
        audio.loop = true;
        audio.volume = 0.25;
        audio.play().catch((err) => console.warn("Audio play failed", err));
        audioRef.current = audio;
      } catch (err) {
        console.warn("Audio init failed", err);
      }
    }

    return () => clearInterval(id);
  }, [derivedBuzzer]);

  const renderCountdownPie = (size = 72, dark = false) => {
    if (typeof countdownMs !== "number") return null;
    const pct = Math.min(100, Math.max(0, (countdownMs / BUZZ_DURATION_MS) * 100));
    const secs = Math.max(0, Math.ceil(countdownMs / 1000));
    const track = dark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.08)";
    const textColor = dark ? "#e2e8f0" : "#0f172a";
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          background: `conic-gradient(#10b981 ${pct}%, ${track} ${pct}% 100%)`,
          display: "grid",
          placeItems: "center",
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          color: textColor,
          fontWeight: 900,
          fontSize: size > 72 ? 22 : 16,
        }}
      >
        {secs}s
      </div>
    );
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: '100vh',
        color: safariPalette.stone,
        ...leopardPrint,
      }}
    >
      <Box sx={{ maxWidth: 1280, mx: 'auto', width: '100%' }}>
      <Grid container spacing={2} justifyContent="center">
        {/* Header + Reset */}
        <Grid item xs={12}>
          <BoardPaper
            elevation={1}
            sx={{
              backgroundColor: 'rgba(255, 247, 230, 0.9)',
              border: '1px solid rgba(31, 20, 11, 0.12)',
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Typography variant="h5" fontWeight={800} color={safariPalette.leatherDeep}>
                {data.title || "Leopardy"}
              </Typography>
              <Stack direction="row" spacing={1.5}>
                {isRealtime && role === "host" && (
                  <IconButton
                    sx={{ color: safariPalette.leatherDeep, backgroundColor: 'rgba(0,0,0,0.04)' }}
                    onClick={resetBoard}
                    title="Reset board"
                  >
                    <RestartAltIcon />
                  </IconButton>
                )}
                {!isRealtime && (
                  <IconButton
                    sx={{ color: safariPalette.leatherDeep, backgroundColor: 'rgba(0,0,0,0.04)' }}
                    onClick={resetBoard}
                    title="Reset board"
                  >
                    <RestartAltIcon />
                  </IconButton>
                )}
              </Stack>
            </Stack>
          </BoardPaper>
        </Grid>

        {/* Scoreboard + Join control */}
        <Grid item xs={12}>
          <BoardPaper elevation={0} sx={{ backgroundColor: 'rgba(255, 247, 230, 0.86)', border: '1px dashed rgba(31, 20, 11, 0.08)' }}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center" justifyContent="center">
                {derivedTeams.length === 0 && (
                  <Muted>
                    {isHost ? "No teams yet — approve join requests here." : "No teams yet — send a join request below."}
                  </Muted>
                )}
                {derivedTeams.map((t, i) => (
                  <Chip
                    key={t.id || i}
                    label={`${t.name}: ${t.score}`}
                    variant="outlined"
                    sx={{
                      fontWeight: 700,
                      backgroundColor: 'rgba(255,255,255,0.82)',
                      color: safariPalette.leatherDeep,
                      borderColor: safariPalette.leather,
                    }}
                  />
                ))}
              </Stack>

              {isHost ? (
                <Stack spacing={1}>
                  <Muted>Join requests</Muted>
                  {derivedPending.length === 0 ? (
                    <Muted>Waiting for players to request to join.</Muted>
                  ) : (
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                      {derivedPending.map((p) => (
                        <Chip
                          key={p.id}
                          label={p.name}
                          onDelete={() => rejectTeam(p.id)}
                          deleteIcon={<CloseIcon />}
                          sx={{
                            backgroundColor: 'rgba(255,255,255,0.9)',
                            border: `1px solid ${safariPalette.leather}`,
                            color: safariPalette.leatherDeep,
                          }}
                          onClick={() => approveTeam(p.id)}
                        />
                      ))}
                    </Stack>
                  )}
                </Stack>
              ) : myTeam ? (
                <Stack spacing={1}>
                  <Muted>Your team</Muted>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      borderRadius: 2,
                      border: "1px solid rgba(31,20,11,0.1)",
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Typography fontWeight={800}>{myTeam.name}</Typography>
                    <Chip label={`$${myTeam.score || 0}`} color="primary" variant="outlined" />
                  </Paper>
                  {myTeam.disabled && (
                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                      You’re locked out for this clue. Wait for the next question.
                    </Alert>
                  )}
                </Stack>
              ) : (
                (!isRealtime || isPlayer || role === "viewer") && (
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="center">
                    <TextField
                      label="Team name"
                      value={joinName}
                      onChange={(e) => setJoinName(e.target.value)}
                      size="small"
                      sx={{ minWidth: 220, backgroundColor: '#fff', borderRadius: 1 }}
                      helperText={isRealtime && !user ? "Sign in to send a request." : ""}
                    />
                    <Button
                      variant="contained"
                      onClick={() => requestJoin(joinName)}
                      disabled={isRealtime && !user}
                      sx={{ backgroundColor: safariPalette.acacia, '&:hover': { backgroundColor: '#5c7d37' } }}
                    >
                      Request to join
                    </Button>
                  </Stack>
                )
              )}
            </Stack>
          </BoardPaper>
        </Grid>

        {isRealtime && derivedActiveClue && (
          <Grid item xs={12}>
            <Alert severity={currentBuzzerTeam ? "info" : "warning"} sx={{ borderRadius: 2, display: "flex", alignItems: "center", gap: 12 }}>
              <span>
                {currentBuzzerTeam
                  ? `${currentBuzzerTeam.name} is answering…`
                  : "Buzzers are open. First team to buzz gets control."}
              </span>
              {renderCountdownPie(48, false)}
            </Alert>
          </Grid>
        )}
        {isRealtime && derivedWinner && (
          <Grid item xs={12}>
            <Alert severity="success" sx={{ borderRadius: 2, fontWeight: 700 }}>
              🎉 {derivedWinner.name} wins with ${derivedWinner.score}!
            </Alert>
          </Grid>
        )}

        {isRealtime && isPlayer && myTeam && derivedActiveClue && (
          <Grid item xs={12}>
            <Button
              fullWidth
              size="large"
              onClick={buzzIn}
              disabled={!canBuzz}
              sx={{
                py: 2.5,
                fontSize: 20,
                fontWeight: 900,
                borderRadius: 3,
                background: canBuzz ? "linear-gradient(135deg, #ef4444, #dc2626)" : "#9ca3af",
                color: "#fff",
                boxShadow: canBuzz ? "0 12px 30px rgba(239,68,68,0.35)" : "none",
                "&:hover": { background: canBuzz ? "#dc2626" : "#9ca3af" },
              }}
            >
              {derivedBuzzer && derivedBuzzer.teamId === myTeam.id
                ? "You’re locked in!"
                : canBuzz
                  ? "Buzz in!"
                  : "Waiting…"}
            </Button>
            <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
              {renderCountdownPie(64, true)}
            </div>
          </Grid>
        )}

        {/* Board */}
        <Grid item xs={12}>
          <BoardPaper
            sx={{
              backgroundColor: 'rgba(255, 247, 230, 0.88)',
              border: '1px solid rgba(31, 20, 11, 0.1)',
              backdropFilter: 'blur(2px)',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${data.categories.length}, minmax(160px, 1fr))`,
                gap: 1.25,
                alignItems: 'stretch',
                justifyItems: 'stretch',
              }}
            >
              {data.categories.map((cat, cIdx) => (
                <Paper
                  key={`header-${cIdx}`}
                  elevation={2}
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: safariPalette.leatherDeep,
                    color: safariPalette.sunrise,
                    border: '1px solid rgba(255,255,255,0.06)',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ textTransform: 'uppercase' }} fontWeight={800}>
                    {cat.name}
                  </Typography>
                </Paper>
              ))}

              {Array.from({ length: rowCount }).map((_, rowIdx) =>
                data.categories.map((cat, cIdx) => {
                  const clue = cat.clues[rowIdx];
                  const cell = derivedBoard[cIdx][rowIdx];
                  if (!clue) {
                    return <Box key={`empty-${cIdx}-${rowIdx}`} />;
                  }
                  return (
                    <TileButton
                      key={`tile-${cIdx}-${rowIdx}`}
                      fullWidth
                      variant="contained"
                      disabled={cell.done || (isRealtime && role !== "host")}
                      onClick={() => pickClue(cIdx, rowIdx)}
                    >
                      {!cell.revealed ? `$${clue.value}` : ''}
                    </TileButton>
                  );
                })
              )}
            </Box>
            <Muted>Tip: Click a tile → reveal question → toggle answer → award/deduct to teams.</Muted>
          </BoardPaper>
        </Grid>
      </Grid>
      </Box>

      {/* Modal for Q/A */}
      <Dialog
        open={Boolean(current)}
        onClose={() => {
          if (isRealtime) return;
          setLocalActiveClue(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(245, 226, 196, 0.96)',
            border: '1px solid rgba(31, 20, 11, 0.14)',
            color: safariPalette.leatherDeep,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 700,
          }}
        >
          <span>{derivedActiveClue ? `${data.categories[derivedActiveClue.catIdx].name} — $${current.value}` : ''}</span>
          {!isRealtime && <IconButton onClick={() => setLocalActiveClue(null)}><CloseIcon /></IconButton>}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="h5" textAlign="center">{current?.prompt}</Typography>
            {role === "host" && (
              <Typography variant="body1" textAlign="center" color="success.main" fontWeight={700} sx={{ mt: 2 }}>
                Answer: {current?.answer}
              </Typography>
            )}
            {role !== "host" && derivedShowAnswer && (
              <Typography variant="h4" textAlign="center" color="success.main" fontWeight={800} sx={{ mt: 2 }}>
                {current?.answer}
              </Typography>
            )}
            {renderCountdownPie(96, true) && (
              <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
                {renderCountdownPie(96, true)}
              </Box>
            )}
          </Box>
          <Divider sx={{ my: 2 }} />
          {(role === "host" || !isRealtime) && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={() => {
                if (isRealtime) {
                    updateDoc(docRef, {
                      "state.showAnswer": !derivedShowAnswer,
                      "state.lastUpdate": serverTimestamp(),
                    }).catch((err) => console.error("toggle answer", err));
                  } else {
                    setLocalShowAnswer((s) => !s);
                  }
                }}
                sx={{ backgroundColor: safariPalette.acacia, color: '#fff', '&:hover': { backgroundColor: '#5c7d37' } }}
              >
                {derivedShowAnswer ? "Hide Answer" : "Show Answer"}
              </Button>
              <Button
                variant="outlined"
                onClick={markDone}
                sx={{ borderColor: safariPalette.leather, color: safariPalette.leatherDeep }}
              >
                Skip
              </Button>
            </Stack>
          )}
          {isRealtime && role === "player" && (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                disabled={!canBuzz}
                onClick={buzzIn}
                sx={{ backgroundColor: safariPalette.acacia, '&:hover': { backgroundColor: '#5c7d37' } }}
              >
                {derivedBuzzer && derivedBuzzer.teamId === myTeam?.id ? "You’re answering…" : "Buzz in"}
              </Button>
            </Stack>
          )}
        </DialogContent>
        {(role === "host" || !isRealtime) && (
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1.5 }}>
            {derivedTeams.map((t, i) => (
              <Stack key={t.id || i} direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={() => isRealtime ? handleCorrect(t.id) : awardPoints(i, +1)}
                  sx={{ backgroundColor: safariPalette.acacia, '&:hover': { backgroundColor: '#5c7d37' } }}
                >
                  +{current?.value} {t.name}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => isRealtime ? handleIncorrect(t.id) : awardPoints(i, -1)}
                  sx={{ backgroundColor: safariPalette.leather, color: '#fff', '&:hover': { backgroundColor: '#7c5127' } }}
                >
                  −{current?.value}
                </Button>
              </Stack>
            ))}
          </DialogActions>
        )}
      </Dialog>

      {isRealtime && gameId && (
        <Box sx={{ maxWidth: 1100, mx: "auto", px: 2, py: 3, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ fontWeight: 700 }}>Share:</div>
          <a href={`/leopardy/host/${gameId}`} style={{ color: "#0ea5e9" }}>Host link</a>
          <a href={`/leopardy/play/${gameId}`} style={{ color: "#0ea5e9" }}>Player link</a>
          <a href={`/leopardy/view/${gameId}`} style={{ color: "#0ea5e9" }}>Viewer link</a>
        </Box>
      )}

    </Box>
  );
}

// Example usage in App.jsx:
// import LeopardyGame from "./LeopardyGame";
// export default function App() { return <LeopardyGame /> }
